import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { reorder } from 'helpers/reorder';
import Checkbox from './checkbox';
import Icon from './icon';
import { useAngularService } from '../../helpers/get_angular_service';
import replaceItem from '../../helpers/replace_item';
import useEffectSkipInitial from '../../helpers/use_effect_skip_initial';
import { PageSettingsColumn } from 'react/types/page_settings';

const getItemStyle = (isDragging, draggableStyle) => ({
  userSelect: 'none',
  background: isDragging ? '#eee' : 'white',
  ...draggableStyle,
});

interface Settings {
  columns: PageSettingsColumn[];
  order_column_name?: string;
  order_direction?: string;
}

interface TableSettingsProps {
  close: () => void;
  settings: Settings;
  page: string;
}
const defaultColumnOrderParams = {
  order_column_name: 'last_name',
  order_direction: 'asc',
};

const TableSettings = ({ close, settings, page }: TableSettingsProps) => {
  const PageSettingsService = useAngularService('PageSettingsService');
  const [columns, setColumns] = useState<PageSettingsColumn[]>(() =>
    settings.columns.filter((item) => !item.hidden),
  );

  const updateColumns = (items) => {
    const newColumns = items.filter((item) => item.display).map((item) => item.name);
    const orderParams =
      settings.order_column_name &&
      ['first_name', 'last_name', ...newColumns].indexOf(settings.order_column_name) < 0
        ? defaultColumnOrderParams
        : {};

    const params = {
      display_column_names: newColumns,
      ...orderParams,
    };

    PageSettingsService.saveSettings(page, params, { skipReload: true });
  };

  useEffectSkipInitial(() => updateColumns(columns), [columns]);

  const onDragEnd = (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    const items = reorder(columns, result.source.index, result.destination.index);

    const wouldDisplaceLockedColumn = items.some((item, newIndex) => {
      if (item.reorderable) return false;
      const oldIndex = columns.findIndex((c) => c.name === item.name);
      return oldIndex !== newIndex;
    });
    if (wouldDisplaceLockedColumn) return;

    setColumns(items);
  };

  const toggleColumn = (column, index) => {
    setColumns((items) => replaceItem(items, { ...column, display: !column.display }, index));
  };

  return (
    <div className="table-settings">
      <div className="table-header table-header--without-search">
        <button type="button" onClick={close} className="table-header__settings">
          <i className="icon-gear" />
          <span>Close</span>
        </button>
      </div>

      <table className="table worx-table worx-table--v2 worx-table--with-header-addon">
        <thead>
          <tr>
            <th colSpan={2}>Table column options (drag to change order)</th>
            <th>Show / hide</th>
          </tr>
        </thead>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided) => (
              <tbody {...provided.droppableProps} ref={provided.innerRef}>
                {columns.map((column, index) => (
                  <Draggable
                    key={column.name}
                    draggableId={column.name}
                    index={index}
                    isDragDisabled={!column.reorderable}
                  >
                    {(provided, snapshot) => (
                      <tr
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                      >
                        <td>
                          {column.reorderable ? (
                            <Icon name="drag" color="#8A8D92" width={24} height={24} />
                          ) : null}
                        </td>
                        <td>{column.label}</td>
                        <td>
                          {!snapshot.isDragging && (
                            <Checkbox
                              disabled={column.display_required}
                              checked={column.display}
                              onChange={() => toggleColumn(column, index)}
                              variant="v2"
                            />
                          )}
                        </td>
                      </tr>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </tbody>
            )}
          </Droppable>
        </DragDropContext>
      </table>
    </div>
  );
};

export default TableSettings;
